import type { Frame, SLink, SNode, SPath, SRing, SText, Scene } from './SceneCanvas.js';

/* ------------------------------------------------------------------ helpers */

function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const clamp = (v: number, a = 0, b = 1) => Math.max(a, Math.min(b, v));
/** Progress of t through [a, b] as 0..1. */
const seg = (t: number, a: number, b: number) => clamp((t - a) / (b - a));
const easeInOut = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const easeOut = (x: number) => 1 - Math.pow(1 - x, 3);
const easeBack = (x: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return x <= 0 ? 0 : 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
};
const lerp = (a: number, b: number, k: number) => a + (b - a) * k;

const hex = (h: string) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)] as const;
function mix(a: string, b: string, k: number) {
  const A = hex(a);
  const B = hex(b);
  const c = (i: number) => Math.round(lerp(A[i]!, B[i]!, clamp(k)));
  return `rgb(${c(0)},${c(1)},${c(2)})`;
}

/** trapmap.fun palette: slate for "independent", the blue scale for the story, red only for danger. */
export const C = {
  solo: '#465066',
  soloHi: '#6b7690',
  line: 'rgba(160,190,235,0.38)',
  dim: 'rgba(255,255,255,0.14)',
  text: '#eef1f6',
  textDim: '#b3bbca',
  faint: '#7d8597',
  blue: '#50a1fa',
  blue2: '#187eec',
  deep: '#0f408c',
  periwinkle: '#8b9dff',
  pink: '#ff5c8a',
  amber: '#f5b13d',
  cyan: '#22d3ee',
  violet: '#a78bfa',
  red: '#ff4d5e',
  brand: '#3d8bff',
};

interface P {
  x: number;
  y: number;
  r: number;
}

/** Scatter circles inside an ellipse without overlaps. */
function scatter(
  rand: () => number,
  radii: number[],
  box: { cx: number; cy: number; rx: number; ry: number },
  taken: P[] = [],
  gap = 6,
  avoid?: { x1: number; y1: number },
): P[] {
  const out: P[] = [];
  for (const r of radii) {
    let best: P | null = null;
    for (let i = 0; i < 400; i++) {
      const a = rand() * Math.PI * 2;
      const d = Math.sqrt(rand());
      const x = box.cx + Math.cos(a) * d * (box.rx - r);
      const y = box.cy + Math.sin(a) * d * (box.ry - r);
      if (avoid && x - r < avoid.x1 && y - r < avoid.y1) continue;
      if ([...taken, ...out].every((p) => Math.hypot(p.x - x, p.y - y) >= p.r + r + gap)) {
        best = { x, y, r };
        break;
      }
    }
    out.push(best ?? { x: box.cx + (rand() - 0.5) * box.rx, y: box.cy + (rand() - 0.5) * box.ry, r });
  }
  return out;
}

/** Circles evenly around a centre at the smallest radius where they don't touch. */
function ring(radii: number[], cx: number, cy: number, inner: number, gap = 3): P[] {
  let R = inner + Math.max(...radii) + gap;
  const width = (r: number) => 2 * Math.asin(clamp((r + gap / 2) / R, 0, 1));
  while (radii.reduce((s, r) => s + width(r), 0) > Math.PI * 2) R += 1;
  const total = radii.reduce((s, r) => s + width(r), 0);
  const spare = (Math.PI * 2 - total) / radii.length;
  let a = -Math.PI / 2;
  return radii.map((r) => {
    const w = width(r);
    const at = a + w / 2;
    a += w + spare;
    return { x: cx + Math.cos(at) * R, y: cy + Math.sin(at) * R, r };
  });
}

/** Greedy circle pack around a centre (largest in the middle). */
function pack(radii: number[], cx: number, cy: number, gap = 2.5): P[] {
  const out: P[] = [];
  for (const r of radii) {
    if (!out.length) {
      out.push({ x: cx, y: cy, r });
      continue;
    }
    let placed: P | null = null;
    for (let d = 0; d < 400 && !placed; d += 1.5) {
      for (let a = 0; a < Math.PI * 2; a += 0.12) {
        const x = cx + Math.cos(a) * d;
        const y = cy + Math.sin(a) * d;
        if (out.every((p) => Math.hypot(p.x - x, p.y - y) >= p.r + r + gap)) {
          placed = { x, y, r };
          break;
        }
      }
    }
    out.push(placed!);
  }
  return out;
}

const float = (t: number, i: number, amp = 1.5) => ({
  dx: Math.sin(t * 0.9 + i * 1.7) * amp,
  dy: Math.cos(t * 0.75 + i * 1.3) * amp,
});

/* ===================================================================== HERO */
/**
 * The whole product in ~11 seconds:
 *   0  "What the chart shows": a crowd of grey holders, looks spread out.
 *   1  "TrapMap traces the money": scan sweep, funders appear, hidden links draw.
 *   2  "What's really there": linked wallets snap into two colour-coded crews.
 */
export const HERO_PHASES = [0, 2.8, 6.0] as const;
export const HERO_PCT = { a: 29, b: 12 };
/** Where the crews end up (scene units), so the page can pin HTML labels to them. */
export const heroZones: { cx: number; cy: number; R: number; color: string; wallets: number; pct: number; w: number; h: number }[] = [];

export const heroScene: Scene = (() => {
  const W = 640;
  const H = 520;
  const rand = rng(11);
  const crews = [
    // Crews finish clear of the overlays: caption top-left, readout bottom-right.
    // Caught crews turn red / orange-red: the same colours the real map uses for flagged groups.
    { color: C.red, cx: 185, cy: 262, radii: [21, 18, 16, 15, 13, 12, 11, 10] },
    { color: '#ff7a45', cx: 488, cy: 168, radii: [17, 15, 13, 11, 10] },
  ];
  const box = { cx: 320, cy: 258, rx: 296, ry: 232 };

  // Crew members start scattered among the crowd: on a price chart they look independent.
  // Top-left corner is under the caption card.
  const caption = { x1: 330, y1: 100 };
  const memberStart = scatter(rand, crews.flatMap((c) => c.radii), box, [], 8, caption);
  const crowdRadii = [16, 14, 12, 11, ...Array.from({ length: 40 }, () => 4 + Math.round(rand() * 6))];
  const crowd = scatter(rand, crowdRadii, box, memberStart, 7, caption);

  let k = 0;
  const members = crews.flatMap((c, ci) => {
    const end = ring(c.radii, c.cx, c.cy, 10, 3.5);
    return c.radii.map((_, j) => ({ ci, j, start: memberStart[k++]!, end: end[j]!, color: c.color }));
  });
  const zones = crews.map((c, ci) => {
    const ms = members.filter((m) => m.ci === ci);
    const R = Math.max(...ms.map((m) => Math.hypot(m.end.x - c.cx, m.end.y - c.cy) + m.end.r));
    return { cx: c.cx, cy: c.cy, R: R + 14 };
  });
  heroZones.push(...zones.map((z, ci) => ({ ...z, color: crews[ci]!.color, wallets: crews[ci]!.radii.length, pct: ci === 0 ? HERO_PCT.a : HERO_PCT.b, w: W, h: H })));
  // Where crowd wallets get nudged so the crews have room to gather.
  const crowdEnd = crowd.map((p, i) => {
    let { x, y } = p;
    for (const z of zones) {
      const d = Math.hypot(x - z.cx, y - z.cy);
      const need = z.R + p.r + 4 - d;
      if (need > 0) {
        const a = d > 0.5 ? Math.atan2(y - z.cy, x - z.cx) : i;
        x += Math.cos(a) * need;
        y += Math.sin(a) * need;
      }
    }
    return { x: clamp(x, p.r + 4, W - p.r - 4), y: clamp(y, p.r + 4, H - p.r - 4) };
  });

  const center = { x: 320, y: 258 };
  const frame = (t: number): Frame => {
    const fadeIn = (i: number) => easeBack(seg(t, 0.05 + i * 0.012, 0.55 + i * 0.012));
    const out = 1 - seg(t, 10.2, 10.9);
    const scanR = easeOut(seg(t, 2.8, 4.1)) * 470;
    const scanO = 0.5 * (1 - seg(t, 2.8, 4.1)) * (t > 2.8 ? 1 : 0);
    const glide = easeInOut(seg(t, 4.8, 6.2));
    const tint = seg(t, 3.9, 4.8);
    const dimCrowd = 1 - 0.5 * seg(t, 4.8, 5.8);
    const hlAt = (x: number, y: number) => (t > 2.8 && t < 4.2 ? clamp(1 - Math.abs(Math.hypot(x - center.x, y - center.y) - scanR) / 34) : 0);

    const nodes: SNode[] = [];
    const links: SLink[] = [];
    const texts: SText[] = [];
    const rings: SRing[] = [{ id: 'scan', cx: center.x, cy: center.y, r: scanR, color: C.brand, o: scanO * out, w: 1.5 }];

    crowd.forEach((p, i) => {
      const f = float(t, i);
      const x = lerp(p.x, crowdEnd[i]!.x, glide) + f.dx;
      const y = lerp(p.y, crowdEnd[i]!.y, glide) + f.dy;
      nodes.push({ id: `c${i}`, x, y, r: p.r * fadeIn(i), fill: C.solo, o: dimCrowd * out, hl: hlAt(x, y), glow: hlAt(x, y) * 0.8 });
    });

    crews.forEach((c, ci) => {
      const show = easeOut(seg(t, 3.4 + ci * 0.2, 3.9 + ci * 0.2));
      nodes.push({ id: `f${ci}`, x: c.cx, y: c.cy, r: 8 * show, fill: c.color, ring: true, stroke: mix(C.soloHi, c.color, tint), o: show * out });
    });

    members.forEach((m, idx) => {
      const f = float(t, idx + 50, 1.2 * (1 - glide) + 0.5);
      const x = lerp(m.start.x, m.end.x, glide) + f.dx;
      const y = lerp(m.start.y, m.end.y, glide) + f.dy;
      const fill = mix(C.solo, m.color, tint);
      nodes.push({ id: `m${idx}`, x, y, r: m.end.r * fadeIn(idx + 3), fill, o: out, hl: hlAt(x, y), glow: Math.max(tint * 0.75, hlAt(x, y) * 0.8) });
      const crew = crews[m.ci]!;
      links.push({
        id: `l${idx}`,
        x1: crew.cx,
        y1: crew.cy,
        x2: x,
        y2: y,
        draw: easeOut(seg(t, 3.55 + idx * 0.07 + m.ci * 0.2, 4.25 + idx * 0.07 + m.ci * 0.2)),
        color: mix('#9fb3d1', m.color, tint),
        o: (0.75 - 0.25 * glide) * out,
        w: 1.3,
      });
    });

    // Dashed perimeter around each crew once it has formed (labels are HTML chips in Landing).
    const lab = seg(t, 6.0, 6.6) * out;
    zones.forEach((z, ci) => {
      const c = crews[ci]!;
      rings.push({ id: `z${ci}`, cx: z.cx, cy: z.cy, r: z.R * (0.9 + 0.1 * easeOut(lab)), color: c.color, o: 0.55 * lab, w: 1.2, dash: '4 5' });
    });
    return { nodes, links, rings, texts };
  };

  return {
    w: W,
    h: H,
    duration: 11,
    frame,
    phase: (t) => (t < HERO_PHASES[1] ? 0 : t < HERO_PHASES[2] ? 1 : 2),
    still: 8,
  };
})();

/* =================================================================== TRAPS */
const TW = 520;
const TH = 340;
const TRAP_T = 9;
const exitFade = (t: number) => 1 - seg(t, 8.3, 8.9);

/** 1. Bundled launch: many wallets bought in the very block the token went live. */
const bundleScene: Scene = (() => {
  const rand = rng(3);
  const launch = { x: 104, y: 292 };
  const bundle = pack([16, 14, 13, 12, 11, 10, 10, 9, 8], 132, 132, 2.5);
  const organic = Array.from({ length: 17 }, (_, i) => {
    const x = 190 + (i / 16) * 290 + (rand() - 0.5) * 10;
    return { x, y: 70 + rand() * 170, r: 3.5 + rand() * 5, at: 1.5 + (i / 16) * 2.4 };
  });
  const frame = (t: number): Frame => {
    const out = exitFade(t);
    const tint = seg(t, 4.2, 5.0);
    const pop = easeBack(seg(t, 0.9, 1.35));
    const paths: SPath[] = [{ id: 'axis', d: `M36 ${launch.y} H488`, color: C.dim, w: 1.5, draw: easeOut(seg(t, 0, 0.8)), o: out }];
    const rings: SRing[] = [
      { id: 'pulse', cx: launch.x, cy: launch.y, r: 4 + easeOut(seg(t, 0.8, 1.7)) * 70, color: C.brand, o: 0.6 * (1 - seg(t, 0.8, 1.7)) * (t > 0.8 ? 1 : 0) * out, w: 1.5 },
    ];
    const nodes: SNode[] = [{ id: 'tick', x: launch.x, y: launch.y, r: 4 * seg(t, 0.6, 0.9), fill: C.brand, o: out, glow: 1 }];
    const links: SLink[] = [];
    bundle.forEach((p, i) => {
      const f = float(t, i, 0.8);
      const x = p.x + f.dx;
      const y = p.y + f.dy;
      links.push({ id: `bl${i}`, x1: launch.x, y1: launch.y, x2: x, y2: y, draw: easeOut(seg(t, 0.9, 1.3)), color: mix('#cdd2dc', C.pink, tint), o: 0.5 * out, w: 1.1 });
      nodes.push({ id: `b${i}`, x, y, r: p.r * pop, fill: mix(C.solo, C.pink, tint), o: out, glow: tint * 0.55 });
    });
    organic.forEach((p, i) => {
      const g = easeBack(seg(t, p.at, p.at + 0.4));
      const f = float(t, i + 20, 0.8);
      links.push({ id: `ol${i}`, x1: p.x, y1: launch.y, x2: p.x + f.dx, y2: p.y + f.dy, draw: easeOut(seg(t, p.at, p.at + 0.3)), color: '#cdd2dc', o: 0.13 * out, w: 1 });
      nodes.push({ id: `o${i}`, x: p.x + f.dx, y: p.y + f.dy, r: p.r * g, fill: C.solo, o: out });
    });
    const tl = seg(t, 0.3, 0.8) * out;
    const texts: SText[] = [
      { id: 'tl', x: launch.x, y: launch.y + 24, text: 'LAUNCH', color: C.brand, o: tl, size: 11, anchor: 'middle', mono: true },
      { id: 't2', x: 300, y: launch.y + 24, text: '+10 MIN', color: C.faint, o: tl, size: 11, anchor: 'middle', mono: true },
      { id: 't3', x: 488, y: launch.y + 24, text: '+1 H', color: C.faint, o: tl, size: 11, anchor: 'end', mono: true },
      { id: 'lab', x: 132, y: 62, text: '9 wallets · same block', color: C.pink, o: seg(t, 4.6, 5.1) * out, size: 12.5, anchor: 'middle', mono: true },
    ];
    return { paths, rings, nodes, links, texts };
  };
  return { w: TW, h: TH, duration: TRAP_T, frame, phase: (t) => (t >= 5.2 ? 1 : 0), still: 7 };
})();

/** 2. Hidden creator: the dev "sold" — into fresh wallets they also control. */
const creatorScene: Scene = (() => {
  const rand = rng(5);
  const creator = { x: 120, y: 170 };
  const fresh = scatter(rand, [17, 15, 14, 13, 12], { cx: 380, cy: 170, rx: 110, ry: 120 }, [], 14);
  const crowd = scatter(rand, Array.from({ length: 13 }, () => 4 + Math.round(rand() * 5)), { cx: 360, cy: 170, rx: 150, ry: 150 }, fresh, 8);
  const frame = (t: number): Frame => {
    const out = exitFade(t);
    const tint = seg(t, 3.6, 4.4);
    const sold = easeInOut(seg(t, 1.6, 3.0));
    const nodes: SNode[] = [];
    const links: SLink[] = [];
    crowd.forEach((p, i) => {
      const f = float(t, i);
      nodes.push({ id: `c${i}`, x: p.x + f.dx, y: p.y + f.dy, r: p.r * easeBack(seg(t, 0.1 + i * 0.04, 0.5 + i * 0.04)), fill: C.solo, o: out * (1 - 0.4 * tint) });
    });
    fresh.forEach((p, i) => {
      const f = float(t, i + 30, 1);
      const x = p.x + f.dx;
      const y = p.y + f.dy;
      links.push({ id: `l${i}`, x1: creator.x, y1: creator.y, x2: x, y2: y, draw: easeOut(seg(t, 1.8 + i * 0.15, 2.4 + i * 0.15)), color: mix('#cdd2dc', C.cyan, tint), o: 0.6 * out, w: 1.3 });
      nodes.push({ id: `n${i}`, x, y, r: p.r * easeBack(seg(t, 2.1 + i * 0.15, 2.7 + i * 0.15)), fill: mix(C.solo, C.cyan, tint), o: out, glow: tint * 0.55 });
    });
    nodes.push({ id: 'dev', x: creator.x, y: creator.y, r: lerp(38, 10, sold) * easeBack(seg(t, 0, 0.5)), fill: mix('#80858f', C.cyan, tint), o: out });
    const texts: SText[] = [
      { id: 'who', x: creator.x, y: creator.y - lerp(38, 10, sold) - 12, text: 'CREATOR', color: mix('#a3a7b1', C.cyan, tint), o: seg(t, 0.3, 0.7) * out, size: 11, anchor: 'middle', mono: true },
      { id: 'q', x: creator.x, y: creator.y + 64, text: '“I sold everything”', color: C.textDim, o: seg(t, 0.6, 1.1) * (1 - seg(t, 3.0, 3.5)) * out, size: 13, anchor: 'middle', weight: 500 },
      { id: 'lab', x: 380, y: 34, text: 'same owner · 5 new wallets', color: C.cyan, o: seg(t, 4.4, 4.9) * out, size: 12.5, anchor: 'middle', mono: true },
    ];
    return { nodes, links, texts };
  };
  return { w: TW, h: TH, duration: TRAP_T, frame, phase: (t) => (t >= 4.8 ? 1 : 0), still: 7 };
})();

/** 3. Coordinated dump: a linked group sells together, the price follows. */
const dumpScene: Scene = (() => {
  const rand = rng(9);
  const hub = { x: 150, y: 112 };
  const crew = ring([18, 16, 15, 13, 12, 11, 10], hub.x, hub.y, 8, 3);
  const crowd = scatter(rand, Array.from({ length: 15 }, () => 4 + Math.round(rand() * 6)), { cx: 360, cy: 112, rx: 150, ry: 92 }, [], 8);
  // Price: rises, then breaks down the moment the group sells.
  const pts = (from: number, to: number, y0: number, y1: number, n: number, seed: number) => {
    const r = rng(seed);
    return Array.from({ length: n + 1 }, (_, i) => {
      const x = lerp(from, to, i / n);
      const y = lerp(y0, y1, i / n) + (i && i < n ? (r() - 0.5) * 12 : 0);
      return `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(' ');
  };
  const sellX = 316;
  const rise = pts(40, sellX, 318, 252, 12, 4);
  const drop = pts(sellX, 488, 252, 322, 8, 8);
  const frame = (t: number): Frame => {
    const out = exitFade(t);
    const dump = easeInOut(seg(t, 2.2, 3.6));
    const nodes: SNode[] = [{ id: 'hub', x: hub.x, y: hub.y, r: 8, fill: C.amber, ring: true, stroke: C.amber, o: seg(t, 0.2, 0.6) * out }];
    const links: SLink[] = [];
    crowd.forEach((p, i) => {
      const f = float(t, i);
      nodes.push({ id: `c${i}`, x: p.x + f.dx, y: p.y + f.dy, r: p.r * easeBack(seg(t, 0.1 + i * 0.03, 0.5 + i * 0.03)), fill: C.solo, o: out });
    });
    crew.forEach((p, i) => {
      const f = float(t, i + 40, 0.8);
      const x = p.x + f.dx;
      const y = p.y + f.dy;
      links.push({ id: `l${i}`, x1: hub.x, y1: hub.y, x2: x, y2: y, draw: easeOut(seg(t, 0.3 + i * 0.05, 0.8 + i * 0.05)), color: C.amber, o: 0.55 * out, w: 1.2 });
      const flash = t > 2.2 && t < 3.8 ? 0.5 + 0.5 * Math.sin((t - 2.2) * 9) : 0;
      nodes.push({ id: `m${i}`, x, y, r: p.r * lerp(1, 0.32, dump) * easeBack(seg(t, 0.1 + i * 0.04, 0.5 + i * 0.04)), fill: mix(C.amber, C.red, dump * 0.55), o: out, hl: flash * 0.8, glow: 0.45 });
    });
    const paths: SPath[] = [
      { id: 'base', d: `M36 336 H488`, color: C.dim, w: 1, o: 0.6 * out },
      { id: 'rise', d: rise, color: C.blue, w: 2.2, draw: easeOut(seg(t, 0.3, 1.9)), o: out },
      { id: 'drop', d: drop, color: C.red, w: 2.2, draw: easeOut(seg(t, 2.3, 3.7)), o: out },
      { id: 'mark', d: `M${sellX} 216 V336`, color: C.red, w: 1, dash: '3 4', o: 0.6 * seg(t, 2.2, 2.5) * out },
    ];
    const texts: SText[] = [
      { id: 'px', x: 40, y: 236, text: 'PRICE', color: C.faint, o: seg(t, 0.3, 0.7) * out, size: 11, mono: true },
      { id: 'sell', x: sellX + 8, y: 230, text: 'GROUP SELLS', color: C.red, o: seg(t, 2.3, 2.7) * out, size: 11, mono: true },
      { id: 'lab', x: hub.x, y: 46, text: '7 linked wallets', color: C.amber, o: seg(t, 0.8, 1.2) * out, size: 12.5, anchor: 'middle', mono: true },
    ];
    return { paths, nodes, links, texts };
  };
  return { w: TW, h: TH, duration: TRAP_T, frame, phase: (t) => (t >= 4.2 ? 1 : 0), still: 7 };
})();

/** 4. Fake crowd: a healthy-looking holder count that one wallet funded. */
const farmScene: Scene = (() => {
  const rand = rng(21);
  const hub = { x: 300, y: 190 };
  const tiny = Array.from({ length: 64 }, (_, i) => {
    const a = (i / 64) * Math.PI * 2 + (rand() - 0.5) * 0.12;
    const d = 46 + rand() * 104;
    return { x: hub.x + Math.cos(a) * d * 1.25, y: hub.y + Math.sin(a) * d * 0.88, r: 2.8 + rand() * 2.4, at: 0.4 + rand() * 2 };
  });
  const big = scatter(rand, [12, 10, 9, 9, 8], { cx: 290, cy: 190, rx: 250, ry: 150 }, tiny.map((p) => ({ ...p, r: p.r + 4 })), 8);
  const frame = (t: number): Frame => {
    const out = exitFade(t);
    const tint = seg(t, 4.0, 4.8);
    const count = Math.round(easeOut(seg(t, 0.4, 2.4)) * 1212);
    const nodes: SNode[] = [];
    const links: SLink[] = [];
    big.forEach((p, i) => {
      const f = float(t, i);
      nodes.push({ id: `g${i}`, x: p.x + f.dx, y: p.y + f.dy, r: p.r * easeBack(seg(t, 0.2 + i * 0.1, 0.6 + i * 0.1)), fill: C.solo, o: out * (1 - 0.35 * tint) });
    });
    tiny.forEach((p, i) => {
      const f = float(t, i, 0.7);
      const x = p.x + f.dx;
      const y = p.y + f.dy;
      links.push({ id: `l${i}`, x1: hub.x, y1: hub.y, x2: x, y2: y, draw: easeOut(seg(t, 3.0 + i * 0.012, 3.5 + i * 0.012)), color: mix('#cdd2dc', C.violet, tint), o: 0.32 * out, w: 0.8 });
      nodes.push({ id: `t${i}`, x, y, r: p.r * easeBack(seg(t, p.at, p.at + 0.35)), fill: mix(C.solo, C.violet, tint), o: out, glow: tint * 0.4 });
    });
    nodes.push({ id: 'hub', x: hub.x, y: hub.y, r: 10 * easeBack(seg(t, 2.8, 3.2)), fill: C.violet, ring: true, stroke: mix(C.soloHi, C.violet, tint), o: out });
    const healthy = seg(t, 2.2, 2.6) * (1 - seg(t, 3.6, 4.0));
    const texts: SText[] = [
      { id: 'hl', x: 28, y: 30, text: 'HOLDERS', color: C.faint, o: seg(t, 0.2, 0.5) * out, size: 11, mono: true },
      { id: 'hc', x: 28, y: 60, text: count.toLocaleString('en-US'), color: C.text, o: seg(t, 0.2, 0.5) * out, size: 28, weight: 700, mono: true },
      { id: 'ok', x: 28, y: 82, text: 'looks healthy', color: C.blue, o: healthy * out, size: 12.5, mono: true },
      { id: 'bad', x: 28, y: 82, text: '64 funded by 1 wallet', color: C.violet, o: seg(t, 4.3, 4.8) * out, size: 12.5, mono: true },
    ];
    return { nodes, links, texts };
  };
  return { w: TW, h: TH, duration: TRAP_T, frame, phase: (t) => (t >= 4.8 ? 1 : 0), still: 7 };
})();

/**
 * Play the build-up faster and hold the verdict longer, so a reader who
 * glances at a trap mostly sees the answer, not the set-up.
 */
function retime(s: Scene, verdictAt: number, speed = 1.3, hold = 5.4): Scene {
  const a = verdictAt / speed;
  const D = a + hold;
  const map = (t: number) => (t < a ? t * speed : t < D - 0.6 ? Math.min(verdictAt + (t - a), 8.25) : 8.3 + (t - (D - 0.6)));
  return { ...s, duration: D, frame: (t) => s.frame(map(t)), phase: (t) => s.phase(map(t)) };
}

export type RiskLevel = 'low' | 'medium' | 'high';
export interface Trap {
  id: string;
  name: string;
  hook: string;
  body: string;
  scene: Scene;
  verdict: { level: RiskLevel; title: string; detail: string };
}

export const TRAPS: Trap[] = [
  {
    id: 'bundle',
    name: 'The bundled launch',
    hook: 'One buyer pretending to be many.',
    body: 'Dozens of wallets buy in the exact block the token goes live. On a chart it looks like demand. On the map they all point back to the same second.',
    scene: retime(bundleScene, 5.2),
    verdict: { level: 'high', title: 'The launch was bundled', detail: '9 wallets bought in the launch block and still hold 31% of the supply.' },
  },
  {
    id: 'creator',
    name: 'The hidden creator',
    hook: '“Dev sold” doesn’t mean the dev is gone.',
    body: 'The creator’s wallet empties, so screeners say the dev sold. The tokens only moved to fresh wallets the same person controls, ready to sell into your buy.',
    scene: retime(creatorScene, 4.8),
    verdict: { level: 'high', title: 'The creator still holds a lot', detail: 'Their “sold” tokens went to 5 new wallets they control: 22% of the supply.' },
  },
  {
    id: 'dump',
    name: 'The insider ring',
    hook: 'Wallets that buy together sell together.',
    body: 'A cluster funded from one source exits at the same time. Their bubbles shrink in sync, and the price follows them down. You see it while it’s happening, not after.',
    scene: retime(dumpScene, 4.2),
    verdict: { level: 'high', title: 'A linked group is selling', detail: '7 wallets funded by one source sold 18% of the supply in 4 minutes.' },
  },
  {
    id: 'farm',
    name: 'The farmed holders',
    hook: 'A healthy holder count, bought by one wallet.',
    body: 'Hundreds of tiny wallets, created minutes before launch and all funded by the same wallet, make the holder count look organic. The map shows they share one source.',
    scene: retime(farmScene, 4.8),
    verdict: { level: 'medium', title: 'Many holders are brand-new wallets', detail: '64 holders were created minutes before launch and funded by one wallet.' },
  },
];
